import { Box } from "@mui/material";
import { blue, green, grey, orange, red } from "@mui/material/colors";
import {
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  RadioButtonUncheckedOutlined,
  ReportProblemOutlined,
} from "@mui/icons-material";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";

/**
 * Primitives shared by the two property-card layouts — the full-detail
 * `PropertyCard` and the dense grid `PropertyCardCompact`. Keeping the tokens,
 * money/date formatters, condition palette and flood-cert state machine here
 * means the compact card reads as the same visual language as the detailed one
 * instead of re-deriving its own.
 */

// Design tokens used by the redesigned property cards. Mirrors the slate /
// editorial palette from the loan summary alt tab.
export const TOKENS = {
  surface: "#ffffff",
  surface2: "#f3f5f8",
  surface3: "#f7f9fb",
  ink: "#0f172a",
  ink2: "#334155",
  ink3: "#64748b",
  ink4: "#94a3b8",
  line: "#d8e0ea",
  line2: "#e6ebf2",
  accent: "#1a3d2e",
  accentSoft: "#e8f0eb",
  accentLine: "#c5dccd",
  warn: "#b8641d",
  warnSoft: "#fbf0e2",
  warnLine: "#e6c8a3",
  urgent: "#a82a2a",
  urgentSoft: "#fae8e8",
  good: "#2d6a4a",
  goodSoft: "#e3efe7",
  goodLine: "#c5dccd",
  info: "#2c5282",
  infoSoft: "#e6edf5",
  infoLine: "#c7d6e9",
};

export const SHADOW_MD =
  "0 2px 4px rgba(15, 23, 42, 0.05), 0 6px 16px rgba(15, 23, 42, 0.07)";
export const SHADOW_LG =
  "0 4px 12px rgba(15, 23, 42, 0.08), 0 16px 40px rgba(15, 23, 42, 0.10)";
export const FONT_SERIF = '"Fraunces", "Source Serif Pro", Georgia, serif';
export const FONT_MONO = '"JetBrains Mono", "SF Mono", Menlo, monospace';

export const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "$0";
  return `$${n.toLocaleString()}`;
};

export const fmtNumber = (v, suffix = "") => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "—";
  return `${n.toLocaleString()}${suffix}`;
};

export const fmtDateShort = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return formatDate(v, "", "MMM d, yyyy");
};

// Annualize a monetary field — picks `annual<Name>` when present, otherwise
// derives it from the matching `monthly<Name>` * 12. Returns null when both
// are zero/missing so the consumer can skip the row entirely.
export const annualOrDerive = (annualVal, monthlyVal) => {
  const a = Number(annualVal) || 0;
  if (a > 0) return a;
  const m = Number(monthlyVal) || 0;
  if (m > 0) return m * 12;
  return null;
};

// Tone for the C1–C6 condition rating. Higher number = worse condition.
export const CONDITION_TONE = {
  C1: { fg: green[800], bg: green[50] },
  C2: { fg: green[700], bg: green[50] },
  C3: { fg: blue[700], bg: blue[50] },
  C4: { fg: orange[800], bg: orange[50] },
  C5: { fg: orange[900], bg: orange[50] },
  C6: { fg: red[700], bg: red[50] },
};

// Sub-line tag pill (uppercase mono, surface-2 fill, accent-aware).
export const SUB_TAG_TONES = {
  muted: { bg: TOKENS.surface2, fg: TOKENS.ink2, border: TOKENS.line },
  info: { bg: TOKENS.infoSoft, fg: TOKENS.info, border: TOKENS.infoLine },
  warn: { bg: TOKENS.warnSoft, fg: TOKENS.warn, border: TOKENS.warnLine },
  urgent: { bg: TOKENS.urgentSoft, fg: TOKENS.urgent, border: "#e8c4c4" },
  good: { bg: TOKENS.goodSoft, fg: TOKENS.good, border: TOKENS.goodLine },
};

export const SubTag = ({ tone = "muted", children, sx }) => {
  const t = SUB_TAG_TONES[tone] || SUB_TAG_TONES.muted;
  return (
    <Box
      component="span"
      sx={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.fg,
        px: "7px",
        py: "2px",
        borderRadius: "4px",
        fontSize: 10.5,
        fontFamily: FONT_MONO,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontWeight: 500,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// Single source of truth for how a `lastFloodCertResult` reads on a card —
// tone, icon and one-line label.
export const getFloodCertChipState = (result) => {
  if (!result) {
    return {
      tone: "neutral",
      icon: RadioButtonUncheckedOutlined,
      label: "Flood cert not ordered",
    };
  }
  const { status, floodZone, followUp } = result;
  if (status === "success") {
    return {
      tone: "success",
      icon: CheckCircleOutline,
      label: floodZone
        ? `Flood cert: Zone ${floodZone}`
        : "Flood cert: completed",
    };
  }
  if (status === "pending") {
    return {
      tone: "pending",
      icon: HourglassEmpty,
      label: "Flood cert: pending vendor",
    };
  }
  if (status === "skipped") {
    return {
      tone: "warning",
      icon: ReportProblemOutlined,
      label: "Flood cert: skipped",
    };
  }
  // status === "error" or unknown
  if (followUp?.category === "auth_failed") {
    return {
      tone: "error",
      icon: ErrorOutline,
      label: "Flood cert: vendor auth issue",
    };
  }
  return {
    tone: "error",
    icon: ErrorOutline,
    label: "Flood cert: action needed",
  };
};

export const FLOOD_TONE_STYLES = {
  neutral: { fg: grey[600], bg: grey[100], border: grey[200] },
  success: { fg: green[800], bg: green[50], border: green[200] },
  pending: { fg: blue[800], bg: blue[50], border: blue[200] },
  warning: { fg: orange[800], bg: orange[50], border: orange[200] },
  error: { fg: red[700], bg: red[50], border: red[200] },
};
